import { config } from "config";

/** Trim and strip accidental `# comment` / extra whitespace from .env (shows up as `%20` in the script URL). */
function sanitizeGoogleMapsApiKey(raw: string | undefined): string {
  if (!raw) return "";
  return String(raw)
    .trim()
    .split("#")[0]
    .trim()
    .split(/\s+/)[0];
}

function placesLibraryReady(): boolean {
  return (
    typeof window !== "undefined" &&
    Boolean(window.google?.maps?.places) &&
    typeof window.google.maps.places.AutocompleteSuggestion
      ?.fetchAutocompleteSuggestions === "function"
  );
}

class MapsLoaderService {
  private static susbcribers: any[] = [];
  static loadingScript = false;
  static mapsLoaded = false;

  static emitMapsLoadEvent() {
    MapsLoaderService.susbcribers.forEach((f: any) => f());
    MapsLoaderService.susbcribers = [];
    MapsLoaderService.mapsLoaded = true;
  }

  static subscribe(f: Function) {
    MapsLoaderService.susbcribers.push(f);
  }

  private static appendScript(url: string, onLoad: () => void) {
    const script = document.createElement("script");
    script.src = url;
    script.async = true;
    script.onload = onLoad;
    script.onerror = () => {
      MapsLoaderService.loadingScript = false;
      console.error("Failed to load Google Maps JavaScript API");
    };
    document.head.appendChild(script);
  }

  /**
   * Ensures a single Maps + Places load. Reuses the tag from `public/index.html` when present;
   * never appends a second `maps/api/js` script (duplicate load breaks the API and AutocompleteSuggestion).
   */
  static LoadScripts() {
    if (MapsLoaderService.mapsLoaded) return;
    if (placesLibraryReady()) {
      MapsLoaderService.emitMapsLoadEvent();
      return;
    }
    if (MapsLoaderService.loadingScript) return;

    const existing = document.querySelector<HTMLScriptElement>(
      'script[src*="maps.googleapis.com/maps/api/js"]'
    );

    if (existing) {
      MapsLoaderService.loadingScript = true;
      const finish = () => {
        if (placesLibraryReady()) {
          MapsLoaderService.emitMapsLoadEvent();
        } else {
          MapsLoaderService.loadingScript = false;
        }
      };
      if (placesLibraryReady()) {
        finish();
        return;
      }
      existing.addEventListener("load", finish);
      existing.addEventListener("error", () => {
        MapsLoaderService.loadingScript = false;
        console.error(
          "Google Maps script from index.html failed (check API key and remove invalid callback= params)"
        );
      });
      return;
    }

    const key = sanitizeGoogleMapsApiKey(config.GOOGLE_MAPS_API_KEY);
    if (!key) {
      console.error(
        "REACT_APP_GOOGLE_MAPS_API_KEY is missing — set it before build/run."
      );
      return;
    }

    MapsLoaderService.loadingScript = true;
    const url = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      key
    )}&libraries=places&loading=async`;
    MapsLoaderService.appendScript(url, () => {
      if (placesLibraryReady()) {
        MapsLoaderService.emitMapsLoadEvent();
      } else {
        MapsLoaderService.loadingScript = false;
      }
    });
  }
}

export default MapsLoaderService;
